<template>
  <Page :not-found="!canViewArtwork && !loading">
    <StickyAskUsBtn v-if="$vuetify.breakpoint.mdAndUp"/>
    <div
      v-if="loading"
      class="text-xs-center my-5 pt-5"
    >
      <v-progress-circular
        :size="50"
        color="black"
        indeterminate
      />
    </div>

    <div v-else>
      <v-container
        fluid
        grid-list-xl
        px-0
        py-2
        class="artwork-container"
      >
        <v-layout
          row
          wrap
        >
          <v-flex
            sm12
            md7
            lg8
          >
            <router-view
              :artwork="artwork"
              @input="artwork === $event"
            />
            <!-- <reaction-picker class="mt-3" :artwork="artwork" /> -->

            <!-- Thumbnails -->
            <Thumbnails
              class="my-4"
              :artwork="artwork"
            />

            <!-- Test walls -->
            <div v-if="artwork.id && artwork.isTestable">
              <v-layout
                row
                align-center
              >
                <v-flex
                  grow
                  pb-0
                >
                  <h2 class="d-inline-block mr-3 my-3">Taiko Test</h2>
                  <v-tooltip
                    dark
                    top
                    max-width="300px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        size="20"
                        color="primary"
                        v-on="on"
                      >fas fa-info-circle
                      </v-icon>
                    </template>
                    <span>{{ $t('TOOLTIP.ARTWORK.TAIKO_TEST') }}</span>
                  </v-tooltip>
                </v-flex>
                <v-flex
                  shrink
                  pb-0
                >
                  <tk-btn
                    flat
                    icon
                    @click="isTestExpanded = !isTestExpanded"
                    aria-label="Toggle taiko test"
                  >
                    <v-icon>{{ isTestExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
                  </tk-btn>
                </v-flex>
              </v-layout>

              <v-slide-y-transition>
                <test-walls
                  v-show="isTestExpanded"
                  :sizeRange="artwork.sizeRange"
                />
              </v-slide-y-transition>
            </div>
          </v-flex>

          <!-- Artwork info -->
          <v-flex
            sm12
            md5
            lg4
            :px-1="$vuetify.breakpoint.smAndDown"
          >
            <artwork-info :artwork="artwork"/>
          </v-flex>
        </v-layout>
      </v-container>

      <!-- Taiko recommends -->
      <div
        class="mt-1"
        v-if="artworks.length"
      >
        <h1
          class="py-4 display-1 font-weight-regular font-miller-display decorated"
          v-html="$t('COMMON.TAIKO_RECOMMENDS')"
        ></h1>

        <masonry
          :cols="{default: 4, 1200: 3,  960: 2, 600: 1}"
          :gutter="60"
        >
          <template v-for="artwork in artworks">
            <tk-artwork
              :key="artwork.id"
              :artwork="artwork"
              :showArtistName="true"
              class="mb-4"
            />
          </template>
        </masonry>
      </div>
    </div>
  </Page>
</template>


<script>
import { RepositoryFactory } from '@/services/RepositoryFactory'
import { ErrorHandler } from '@/utils/ErrorHandler'
import Page from '../../../components/base/Page/Page'
import { ArtistStatus } from '../../../services/artist/ArtistStatus'
import { useGTag } from '../../../services/google/google-tag-manager'

const artworksRepo = RepositoryFactory.artworks()
const searchRepo = RepositoryFactory.search()
const handler = new ErrorHandler()

export default {
  name: 'ArtistArtworkViewPage',

  props: {
    artworkId: { type: String, required: true },
  },

  components: {
    Page,
    /* ReactionPicker: () => import("../../../components/Domain/ReactionPicker"), */
    ArtworkInfo: () => import('./ArtworkInfo'),
    TestWalls: () => import('./Test/TestWalls'),
    Thumbnails: () => import('./Thumbnails'),
    StickyAskUsBtn: () => import('@/components/core/StickyAskUsBtn'),
  },
  data () {
    return {
      loading: false,
      artwork: {
        artist: {},
      },
      artworks: [],
      isTestExpanded: true,
    }
  },

  watch: {
    /* $route: function(val) {
      if (val.name === "artist.artwork") this.init();
    } */
    '$route.params.artworkId': 'init',
  },

  created () {
    this.init()
  },

  computed: {
    canViewArtwork () {
      const currentUser = this.$auth.currentUser
      console.log('currentUser',currentUser)
      const artistApproved = this.artwork && this.artwork.artist  && this.artwork.artist.status === ArtistStatus.Approved
      return !this.artwork.artist || artistApproved || (currentUser && currentUser.isAdmin) || false
    }
  },

  methods: {
    init () {
      this.fetchData()
    },

    async fetchData () {
      this.loading = true
      await handler.handle(async () => {
        this.artwork = await artworksRepo.getArtwork(this.artworkId)
        // Pixel
        if (window.fbq && process.env.VUE_APP_ENV === 'production') {
          console.log('sending pixel tracking')
          const price = this.artwork.price
            ? this.artwork.price.replaceAll(',', '.')
            : null
          fbq('track', 'ViewContent', {
            content_ids: this.artwork.id,
            content_type: 'product_group',
            value: price,
            currency: 'EUR',
          })
        }
        useGTag().viewItem(this.artwork)

        this.$app.setTitle(this.artwork.title)

        this.fetchSimilarArtworks(this.artwork)
      })
      this.loading = false
    },

    async fetchSimilarArtworks (artwork) {
      await handler.handle(async () => {
        this.artworks = await searchRepo.similarArtworks(
          artwork,
          this.$i18n.locale
        )
      })
    },
  },
}
</script>


<style
  lang="scss"
  scoped
>
.artwork-container {
  min-height: 780px;
}
</style>
